import React, { useEffect, useRef, useState, useTransition } from 'react';
import {
    Container,
    Box,
    SxProps,
    Theme,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Typography,
    Rating,
    Stack,
    Button,
    IconButton,
    useMediaQuery,
} from '@mui/material';
import { ITalentReview, IWorkProfile } from '../../types/global/helper';
import { FONT_ENUM } from '../../types/global/fonts.types';
import WorkProfileHeader from './WorkProfileHeader';
import { CustomSegmentedControl } from '../SegmentedControl/CustomSegmentedControl';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { CastingProfile, CastingProfileSaveHandle, TUpdateBody } from './CastingProfile';
import { WorkProfileEditFocusProvider } from './WorkProfileEditFocusContext';
import { Media } from './Media';
import { Reviews } from './Reviews';
import { WorkProfileSelector } from './WorkProfileSelector';
import { useDispatch } from 'react-redux';
import {
    createTalentReview,
    // getTalentReviews,
    updateTalentProfile,
    updateTalentReview,
    deleteTalentReview,
    likeTalentReview,
    dislikeTalentReview,
} from '../../redux/talentProfile/action';
import { deleteLike } from '../../redux/project/actions';
import CustomButton from '../Button/CustomButton';
import Favorite from '@mui/icons-material/Favorite';
import Close from '@mui/icons-material/Close';
import Mail from '@mui/icons-material/Mail';
import { IApplication } from '../../types/global/ListRender.types';
import { ICreateTalentReview } from '../../redux/talentProfile/types';
import { DialogueSheet } from '../DialogueSheet/DialogueSheet';
import { catchAsync, getIdForIbjects, useDispatchPromise } from '../../utils/AppUtils';
import { toggleAuthModal } from '../../redux/authentication/action';
import { useAuth } from '../../module/AuthContext';
import { CreditsTimeline } from './CreditsTimeline';
import { MessageDialogue } from '../MessageDialogue/MessageDialogue';

const CHAR_LIMIT = 300;
const SUBJECT_LIMIT = 50;
const SHOW_WORK_PROFILE_MESSAGE_BUTTON = false;

export interface IWorkProfileProps {
    workProfile?: IWorkProfile[];
    isSummary?: boolean;
    reviews?: ITalentReview[];
    external?: boolean;
    isEditMode?: boolean;
    /** Determines when this is being viewed by a casting agency or poster */
    isPreviewMode?: boolean;
    applicationData?: IApplication;
    onAcceptApplication?: () => void;
    onRejectApplication?: () => void;
    onMessageApplication?: () => void;
    searchFlow?: boolean;
    isPublicMode?: boolean;
    userName?: string;
}

const SUMMARY_TAB = ['Details & Media', 'Reviews', 'Credits'];
const DETAILS_TAB = ['Details', 'Media', 'Reviews', 'Credits'];

// Add this new component for the preview mode action buttons
interface PreviewModeActionsProps {
    onAcceptClick?: () => void;
    onRejectClick?: () => void;
    onMessageClick?: () => void;
    mainSx?: SxProps<Theme>;
    searchFlow?: boolean;
    isPublicMode?: boolean;
}

const PreviewModeActions: React.FC<PreviewModeActionsProps> = ({
    onAcceptClick,
    onRejectClick,
    onMessageClick,
    mainSx,
    searchFlow,
    isPublicMode,
}) => {
    const { themeStyle } = useThemeContext();
    const { isSignedIn } = useAuth();
    const dispatch = useDispatch();

    if (!isSignedIn) {
        return (
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                m={2}
                sx={{
                    position: 'relative',
                    backgroundColor: theme[themeStyle].form_gray,
                    padding: '15px',
                    borderRadius: 4,
                    border: `1px solid ${theme[themeStyle].tag_white}`,
                }}>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.REGULAR_HEADER,
                        color: theme[themeStyle].tag_white,
                        fontSize: {
                            xs: 8,
                            sm: 12,
                        },
                    }}>
                    Sign In to contact talent.
                </Typography>
                <CustomButton
                    label="Sign In"
                    onClick={() => dispatch(toggleAuthModal(true))}
                    sx={{
                        mx: 1,
                        '&:hover': {
                            WebkitTextFillColor: theme[themeStyle].primary_orange,
                        },
                    }}
                    textStyle={{ fontSize: 12 }}
                    customStyle={{ height: 20 }}
                />
            </Stack>
        );
    }

    if (isPublicMode) {
        if (!SHOW_WORK_PROFILE_MESSAGE_BUTTON) return null;
        return (
            <Box
                display="flex"
                alignItems="center"
                gap={2}
                width={'100%'}
                justifyContent={'flex-start'}
                p={2}
                sx={mainSx}>
                <CustomButton
                    label="Message"
                    withIcon={true}
                    beginningIcon={<Mail />}
                    onClick={onMessageClick}
                    buttonProps={{ variant: 'outlined' }}
                    textStyle={{
                        color: theme[themeStyle].main_blue,
                    }}
                    sx={{
                        borderColor: theme[themeStyle].primary,
                        color: theme[themeStyle].primary,
                        '&:hover': {
                            borderColor: theme[themeStyle].primary,
                            backgroundColor: 'rgba(33, 150, 243, 0.1)',
                        },
                    }}
                />
            </Box>
        );
    }

    return (
        <Box display="flex" alignItems="center" gap={2} width={'100%'} justifyContent={'flex-end'} p={2} sx={mainSx}>
            {!searchFlow && (
                <CustomButton
                    label="Reject"
                    withIcon={true}
                    beginningIcon={<Close />}
                    onClick={onRejectClick}
                    buttonProps={{ variant: 'outlined' }}
                    textStyle={{
                        color: theme[themeStyle].secondary_red,
                    }}
                    sx={{
                        borderColor: theme[themeStyle].primary_red,
                        color: theme[themeStyle].secondary_red,
                        '&:hover': {
                            borderColor: theme[themeStyle].primary_red,
                            backgroundColor: 'rgba(244, 67, 54, 0.1)',
                        },
                    }}
                />
            )}
            {!searchFlow && (
                <CustomButton
                    label="Accept"
                    withIcon={true}
                    beginningIcon={<Favorite />}
                    onClick={onAcceptClick}
                    buttonProps={{ variant: 'outlined' }}
                    textStyle={{
                        color: theme[themeStyle].switchGreen,
                    }}
                    sx={{
                        borderColor: theme[themeStyle].tertiary_green,
                        color: theme[themeStyle].tertiary_green,
                        '&:hover': {
                            borderColor: theme[themeStyle].tertiary_green,
                            backgroundColor: 'rgba(76, 175, 80, 0.1)',
                        },
                    }}
                />
            )}
            {SHOW_WORK_PROFILE_MESSAGE_BUTTON && (
                <CustomButton
                    label="Message"
                    withIcon={true}
                    beginningIcon={<Mail />}
                    onClick={onMessageClick}
                    buttonProps={{ variant: 'outlined' }}
                    textStyle={{
                        color: theme[themeStyle].main_blue,
                    }}
                    sx={{
                        borderColor: theme[themeStyle].primary,
                        color: theme[themeStyle].primary,
                        '&:hover': {
                            borderColor: theme[themeStyle].primary,
                            backgroundColor: 'rgba(33, 150, 243, 0.1)',
                        },
                    }}
                />
            )}
        </Box>
    );
};

const ReviewModal: React.FC<{
    open: boolean;
    onClose: () => void;
    onSubmit: (data: ICreateTalentReview) => void;
    talentId: string;
    initialRating?: number;
    initialSubject?: string;
    initialText?: string;
}> = ({ open, onClose, onSubmit, talentId, initialRating, initialSubject, initialText }) => {
    const [rating, setRating] = useState<number | null>(initialRating ?? 2.5);
    const [reviewSubject, setReviewSubject] = useState(initialSubject ?? '');
    const [reviewText, setReviewText] = useState(initialText ?? '');
    const { themeStyle } = useThemeContext();

    // Reset modal state when opened or when editing different review
    React.useEffect(() => {
        if (open) {
            setRating(initialRating ?? 2.5);
            setReviewSubject(initialSubject ?? '');
            setReviewText(initialText ?? '');
        }
    }, [open, initialRating, initialSubject, initialText]);

    const handleSubmit = () => {
        if (talentId) {
            onSubmit({
                rating: rating ?? 2.5,
                review_subject: reviewSubject,
                review_text: reviewText,
                talent: talentId,
            });
            setRating(2.5);
            setReviewSubject('');
            setReviewText('');
            onClose();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '14px',
                    overflow: 'hidden',
                    backgroundColor: '#1a1a1a',
                    border: '1px solid rgba(255,255,255,0.06)',
                    boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
                },
            }}>
            <DialogTitle
                sx={{
                    p: 0,
                    bgcolor: '#1a1a1a',
                }}>
                <Box
                    sx={{
                        height: 120,
                        width: '100%',
                        position: 'relative',
                        background:
                            'linear-gradient(135deg, rgba(250,175,63,0.35) 0%, rgba(216,146,34,0.18) 45%, rgba(26,26,26,0.95) 100%)',
                    }}>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            color: 'rgba(255,255,255,0.7)',
                            backgroundColor: 'rgba(0,0,0,0.35)',
                            '&:hover': { color: theme[themeStyle].tag_white, backgroundColor: 'rgba(0,0,0,0.5)' },
                        }}>
                        <Close />
                    </IconButton>
                    <Box sx={{ position: 'absolute', left: 24, right: 24, bottom: 14 }}>
                        <Typography
                            sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 30, color: theme[themeStyle].tag_white }}>
                            Submit a <span style={{ color: theme[themeStyle].highlight }}>Review</span>
                        </Typography>
                        <Typography
                            sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 13, color: 'rgba(255,255,255,0.6)' }}>
                            Your feedback shapes the future of cinematic casting.
                        </Typography>
                    </Box>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ bgcolor: '#1a1a1a', px: 3, pt: 3, pb: 1 }}>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 10,
                        letterSpacing: '0.14em',
                        textTransform: 'uppercase',
                        color: theme[themeStyle].highlight,
                        mb: 1.5,
                        mt: 3,
                    }}>
                    Performance Rating
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Rating
                        value={rating}
                        onChange={(_, newValue) => setRating(newValue)}
                        precision={0.5}
                        max={5}
                        sx={{
                            fontSize: 34,
                            '& .MuiRating-iconFilled': { color: theme[themeStyle].highlight },
                            '& .MuiRating-iconHover': { color: theme[themeStyle].highlight },
                            '& .MuiRating-iconEmpty': { color: 'rgba(255,255,255,0.22)' },
                        }}
                    />
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: theme[themeStyle].highlight }}>
                        {rating?.toFixed(1) ?? '0.0'}
                    </Typography>
                </Box>
                <TextField
                    label="Review Subject"
                    value={reviewSubject}
                    onChange={e => {
                        const value = e.target.value;
                        if (value.length <= SUBJECT_LIMIT) setReviewSubject(value);
                    }}
                    fullWidth
                    InputLabelProps={{
                        sx: {
                            color: 'rgba(255,255,255,0.5)',
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        },
                    }}
                    InputProps={{
                        sx: {
                            color: theme[themeStyle].tag_white,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            borderRadius: '12px',
                        },
                    }}
                    sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: '#0f0f0f',
                            '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
                            '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.14)' },
                            '&.Mui-focused fieldset': { borderColor: `${theme[themeStyle].highlight}55` },
                        },
                    }}
                />
                <TextField
                    label="Review"
                    value={reviewText}
                    onChange={e => {
                        const value = e.target.value;
                        if (value.length <= CHAR_LIMIT) setReviewText(value);
                    }}
                    fullWidth
                    multiline
                    minRows={4}
                    InputLabelProps={{
                        sx: {
                            color: 'rgba(255,255,255,0.5)',
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        },
                    }}
                    InputProps={{
                        sx: {
                            color: theme[themeStyle].tag_white,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            borderRadius: '12px',
                        },
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: '#0f0f0f',
                            '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
                            '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.14)' },
                            '&.Mui-focused fieldset': { borderColor: `${theme[themeStyle].highlight}55` },
                        },
                        mb: 1,
                    }}
                />
                <Typography sx={{ textAlign: 'right', color: 'rgba(255,255,255,0.45)', fontSize: 12 }}>
                    {reviewText.length}/{CHAR_LIMIT}
                </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, bgcolor: '#1a1a1a', color: theme[themeStyle].tag_white }}>
                <Button
                    onClick={onClose}
                    sx={{
                        color: 'rgba(255,255,255,0.6)',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        textTransform: 'none',
                        '&:hover': { color: theme[themeStyle].tag_white, backgroundColor: 'transparent' },
                    }}>
                    Cancel
                </Button>
                <Button
                    disabled={!rating || !reviewSubject || !reviewText}
                    onClick={handleSubmit}
                    sx={{
                        borderRadius: '999px',
                        px: 3.5,
                        py: 1.1,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        textTransform: 'none',
                        color: theme[themeStyle].black,
                        background: `linear-gradient(135deg, ${theme[themeStyle].highlight} 0%, ${theme[themeStyle].primary_orange} 100%)`,
                        '&:hover': { opacity: 0.94 },
                        '&.Mui-disabled': { opacity: 0.45, color: theme[themeStyle].black },
                    }}>
                    Submit
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const WorkProfileWrapper: React.FC<IWorkProfileProps> = ({
    workProfile,
    isSummary,
    reviews,
    external,
    isEditMode,
    isPreviewMode,
    applicationData,
    onAcceptApplication,
    onRejectApplication,
    onMessageApplication,
    searchFlow,
    isPublicMode,
    userName,
}) => {
    const [, startTransition] = useTransition();
    const { isSignedIn } = useAuth();
    const [slideName, setSlideName] = React.useState<string>(isSummary ? SUMMARY_TAB[0] : DETAILS_TAB[0]);
    const { themeStyle } = useThemeContext();
    const isMobile = useMediaQuery('(max-width:599px)');
    const [selectedWorkProfile, setSelectedWorkProfile] = useState(workProfile && workProfile[0]);
    const [editMode, setEditMode] = useState(false);
    const [heroHasUnsavedMedia, setHeroHasUnsavedMedia] = useState(false);
    const dispatch = useDispatch();
    const dispatchPromise = useDispatchPromise();
    const [reviewModalOpen, setReviewModalOpen] = useState(false);
    const [editingReview, setEditingReview] = useState<ITalentReview | null>(null);
    const [deleteReview, setDeleteReview] = useState<ITalentReview | null>(null);
    const [showMessage, setShowMessage] = useState(false);
    const castingProfileRef = useRef<CastingProfileSaveHandle>(null);

    const handleUpdate = () => {
        if (!heroHasUnsavedMedia) return;
        const payload = castingProfileRef.current?.getMediaSavePayload();
        handleProfileUpdate(payload ?? { body: {}, uploads: undefined });
    };

    useEffect(() => {
        if (!editMode) {
            setHeroHasUnsavedMedia(false);
        }
    }, [editMode]);

    useEffect(() => {
        if (isEditMode) setEditMode(isEditMode);
    }, [isEditMode]);

    useEffect(() => {
        if (editMode && slideName !== 'Details') {
            setEditMode(false);
        }
    }, [editMode, slideName]);

    const handleProfileUpdate = catchAsync(async (payload?: TUpdateBody) => {
        if (workProfile && selectedWorkProfile) {
            const data: IWorkProfile = (await dispatchPromise(
                updateTalentProfile({
                    id: selectedWorkProfile?._id,
                    body: payload?.body,
                    uploads: payload?.uploads,
                    fromCreate: false,
                    /**
                     * This value here is the same cache that is stored in the backend.
                     * To understand this, trace the api call and see the cache value.
                     */
                    cache: `${userName}-profile`,
                }),
            )) as IWorkProfile;
            setEditMode(false);
            setSelectedWorkProfile(data);
        }
    });

    const handleLeaveReview = (data: ICreateTalentReview) => {
        dispatch(
            createTalentReview({
                ...data,
                /**
                 * This value here is the same cache that is stored in the backend.
                 * To understand this, trace the api call and see the cache value.
                 */
                cache: [`${userName}-profile`, `TALENT_REVIEWS_${data.talent}`],
            }),
        );
    };

    const handleOnProfileSelect = (profile: IWorkProfile) => {
        if (profile && profile._id) {
            // Removed as was triggering a second api call
            // dispatch(getTalentReviews({ talentId: profile._id, limit: 10, page: 1 }));
            setSelectedWorkProfile(profile);
        }
    };

    const handleEditReview = (review: ITalentReview) => {
        setEditingReview(review);
        setReviewModalOpen(true);
    };
    const handleDeleteReview = (review: ITalentReview) => {
        setDeleteReview(review);
    };
    const handleConfirmDelete = () => {
        if (deleteReview && deleteReview._id) {
            dispatch(deleteTalentReview(deleteReview._id));
            setDeleteReview(null);
        }
    };
    const handleCancelDelete = () => {
        setDeleteReview(null);
    };
    const handleEditReviewSubmit = (data: ICreateTalentReview) => {
        if (editingReview) {
            dispatch(
                updateTalentReview({
                    id: editingReview._id,
                    review_subject: data.review_subject,
                    review_text: data.review_text,
                    rating: data.rating,
                    cache: [`${userName}-profile`, `TALENT_REVIEWS_${data.talent}`],
                }),
            );
            setEditingReview(null);
            setReviewModalOpen(false);
        }
    };
    const handleLikeReview = (review: ITalentReview) => {
        dispatch(
            likeTalentReview({
                reviewId: review._id,
                type: 'like',
                cache: [`TALENT_REVIEWS_${getIdForIbjects(review.talent)}`],
            }),
        );
    };
    const handleDislikeReview = (review: ITalentReview) => {
        dispatch(
            dislikeTalentReview({
                reviewId: review._id,
                type: 'dislike',
                cache: [`TALENT_REVIEWS_${getIdForIbjects(review.talent)}`],
            }),
        );
    };
    const handleDeleteLike = (likeId: string, review: ITalentReview) => {
        dispatch(
            deleteLike({
                documentId: likeId,
                morePayload: {
                    like_category: 'talentReview',
                    like_type: review.user_has_liked ? 'like' : 'dislike',
                    reviewId: review._id,
                },
            }),
        );
    };

    const handleOnMessage = () => {
        setShowMessage(!showMessage);
    };

    return (
        <>
            <Container disableGutters maxWidth={false} sx={{ width: '100%', maxWidth: '100%' }}>
                {selectedWorkProfile && (
                    <WorkProfileHeader
                        profile={selectedWorkProfile}
                        editMode={editMode}
                        hideRating={
                            selectedWorkProfile?.profile_type !== 'artist' &&
                            selectedWorkProfile?.profile_type !== 'voiceover'
                        }
                        showHeroEditButton={isSignedIn && !isPreviewMode && slideName === 'Details'}
                        showHeroSaveButton={isSignedIn && !isPreviewMode && !!editMode && slideName === 'Details'}
                        heroEditLabel={editMode ? 'Cancel' : 'Edit'}
                        heroSaveLabel="Save"
                        heroSaveDisabled={editMode && !heroHasUnsavedMedia}
                        onHeroEditClick={() => setEditMode(!editMode)}
                        onHeroSaveClick={handleUpdate}
                        onImageChange={(url, file) =>
                            handleProfileUpdate({
                                uploads: {
                                    feature_picture: [file],
                                    temp_feature_picture: url,
                                },
                                body: undefined,
                            })
                        }
                    />
                )}
                <Box
                    sx={{
                        px: { xs: 0, sm: 3 },
                        /* Match CastingProfile inner Container inset on Details (all profile types). */
                        pl: { xs: 0, sm: external ? 3 : 6 },
                        pt: { xs: 0, sm: 4.5 },
                        pb: 0,
                        width: '100%',
                        maxWidth: 'min(1280px, 100%)',
                        mx: 'auto',
                        /* Exactly match page content background (/div[3]). */
                        bgcolor: theme[themeStyle].form_gray,
                    }}>
                    <Stack
                        direction={{ xs: 'column', lg: 'row' }}
                        spacing={{ xs: 3, lg: 3 }}
                        alignItems={{ xs: 'flex-start', lg: 'center' }}
                        justifyContent="space-between"
                        sx={{ mb: 0, gap: { xs: 2.5, lg: 3 } }}>
                        <Box
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                order: { xs: 2, md: 1 },
                                width: { xs: '100%', md: 'auto' },
                            }}>
                            <CustomSegmentedControl
                                variant="underline"
                                tabs={[...(isSummary ? SUMMARY_TAB : DETAILS_TAB)]}
                                selectedTab={slideName}
                                innerContainerStyle={{
                                    width: '100%',
                                }}
                                innerContainerSx={{
                                    justifyContent: { xs: 'space-between', sm: 'flex-start' },
                                    width: { xs: '100%', sm: 'fit-content' },
                                    gap: { xs: 0, sm: 4 },
                                }}
                                typographySx={{
                                    fontSize: { xs: 12, sm: 15 },
                                    whiteSpace: 'nowrap',
                                }}
                                containerStyle={{
                                    backgroundColor: 'transparent',
                                }}
                                sx={{
                                    position: 'static',
                                    justifyContent: 'flex-start',
                                    width: '100%',
                                    mt: 0,
                                    px: { xs: 2, sm: 0 },
                                    boxSizing: 'border-box',
                                }}
                                disregardParam
                                onHandleClick={(tab, index) =>
                                    startTransition(() => {
                                        setSlideName(tab);
                                    })
                                }
                            />
                        </Box>
                        <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                            justifyContent={{ xs: 'stretch', lg: 'flex-end' }}
                            flexWrap="wrap"
                            sx={{
                                order: { xs: 1, lg: 2 },
                                flexShrink: 0,
                                width: { xs: '100%', lg: 'auto' },
                            }}>
                            {!isPreviewMode && workProfile ? (
                                <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                                    <WorkProfileSelector
                                        profiles={workProfile}
                                        onProfileSelect={handleOnProfileSelect}
                                        onEditClick={() => setEditMode(!editMode)}
                                        onCancelClick={() => setEditMode(!editMode)}
                                        external={external}
                                        isEditMode={editMode}
                                        hideEditButton
                                        mainSx={{ p: 0, gap: 1.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}
                                        sx={{
                                            width: { xs: '100%', lg: 256 },
                                            minWidth: { lg: 200 },
                                        }}
                                    />
                                </Box>
                            ) : (
                                <PreviewModeActions
                                    onAcceptClick={onAcceptApplication}
                                    onRejectClick={onRejectApplication}
                                    onMessageClick={onMessageApplication}
                                    searchFlow={searchFlow}
                                />
                            )}
                            {isPublicMode && external && (
                                <PreviewModeActions
                                    onMessageClick={handleOnMessage}
                                    searchFlow={searchFlow}
                                    isPublicMode={isPublicMode}
                                />
                            )}
                        </Stack>
                    </Stack>
                </Box>
                <Box
                    sx={{
                        px: { xs: 2, sm: 3 },
                        pt: { xs: 4, sm: 5 },
                        pb: { xs: 'calc(env(safe-area-inset-bottom, 0px) + 136px)', sm: 4 },
                        width: '100%',
                        maxWidth: 'min(1280px, 100%)',
                        mx: 'auto',
                        bgcolor: theme[themeStyle].form_gray,
                    }}>
                    {slideName === 'Details' && selectedWorkProfile && (
                        <WorkProfileEditFocusProvider>
                            <CastingProfile
                                ref={castingProfileRef}
                                workProfile={selectedWorkProfile}
                                isEditMode={editMode}
                                onHeroUnsavedMediaChange={setHeroHasUnsavedMedia}
                                onChange={handleProfileUpdate}
                                isPreviewMode={isPreviewMode}
                                applicationData={applicationData}
                            />
                        </WorkProfileEditFocusProvider>
                    )}
                    {slideName === 'Media' && (
                        <Media workProfile={selectedWorkProfile} visitorView={!!external} />
                    )}
                    {slideName === 'Reviews' && (
                        <>
                            <Reviews
                                reviews={reviews}
                                profileUserId={selectedWorkProfile?._id}
                                onAddReview={() => setReviewModalOpen(true)}
                                onEditReview={handleEditReview}
                                onDeleteReview={handleDeleteReview}
                                onLikeReview={handleLikeReview}
                                onDislikeReview={handleDislikeReview}
                                onDeleteLikeReview={handleDeleteLike}
                            />
                            <ReviewModal
                                open={reviewModalOpen}
                                onClose={() => {
                                    setReviewModalOpen(false);
                                    setEditingReview(null);
                                }}
                                onSubmit={editingReview ? handleEditReviewSubmit : handleLeaveReview}
                                talentId={selectedWorkProfile?._id!}
                                initialRating={editingReview ? editingReview.rating : undefined}
                                initialSubject={editingReview ? editingReview.review_subject : undefined}
                                initialText={editingReview ? editingReview.review_text : undefined}
                            />
                            <DialogueSheet
                                open={!!deleteReview}
                                onClose={handleCancelDelete}
                                onDisagreeClick={handleCancelDelete}
                                onAgreeClicked={handleConfirmDelete}
                                header={'Delete Review'}
                                body={
                                    'Warning: This action is irreversible!\nAre you sure you want to delete this review? This cannot be undone.'
                                }
                                negativeText={'Cancel'}
                                positiveText={'Delete'}
                                negtiveTextStyle={{ color: undefined }}
                                positiveTextStyle={{ color: '#ff1744' }}
                            />
                        </>
                    )}
                    {slideName === 'Credits' && selectedWorkProfile && (
                        <CreditsTimeline
                            themeStyle={themeStyle}
                            canEdit={!external}
                            workProfile={selectedWorkProfile!}
                        />
                    )}
                </Box>
                {isMobile && !isPreviewMode && workProfile ? (
                    <Box
                        sx={{
                            position: 'fixed',
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 35,
                            px: 2,
                            pt: 1.25,
                            pb: 'calc(env(safe-area-inset-bottom, 0px) + 10px)',
                            background: theme[themeStyle].secondaryBg,
                            borderTopLeftRadius: 16,
                            borderTopRightRadius: 16,
                            boxShadow: '0 -10px 28px rgba(0,0,0,0.35)',
                        }}>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 11,
                                letterSpacing: '0.08em',
                                textTransform: 'uppercase',
                                color: 'rgba(255,255,255,0.62)',
                                mb: 1,
                                px: 0.25,
                            }}>
                            Select Profile Type
                        </Typography>
                        <WorkProfileSelector
                            profiles={workProfile}
                            onProfileSelect={handleOnProfileSelect}
                            onEditClick={() => setEditMode(!editMode)}
                            onCancelClick={() => setEditMode(!editMode)}
                            external={external}
                            isEditMode={editMode}
                            hideEditButton
                            mainSx={{ p: 0, gap: 1, justifyContent: 'stretch' }}
                            sx={{ width: '100%' }}
                        />
                    </Box>
                ) : null}

                {/* Message Dialogue */}
                {selectedWorkProfile && (
                    <MessageDialogue
                        open={showMessage}
                        onClose={handleOnMessage}
                        recipient={selectedWorkProfile.created_by}
                    />
                )}
            </Container>
        </>
    );
};

export { WorkProfileWrapper };
